import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { WindowService } from 'src/shared/providers/window.service';

import { scrollContentWidth } from 'src/shared/components/scroll-wrapper/scroll-wrapper.component';
import { EmployerService } from 'src/providers/employer/employer';

import { TidyCurrencyPipe } from 'src/shared/pipes/tidy-currency.pipe';

import { EmployerEditEmployeePage } from 'src/pages/employer/edit-employee/edit-employee';
import { EmployerAddEmployeePage } from 'src/pages/employer/add-employee/add-employee';
import { EmployerAddCreditPage } from 'src/pages/employer/add-credit/add-credit';

@Component({
    templateUrl: 'dashboard.html',
    styleUrls: ['./dashboard.scss'],
    standalone: false
})

export class EmployerDashboardPage implements OnInit {

  showLoadingSpinner: boolean;
  companyCredit: number;
  readonly desktopWidthContent: string = scrollContentWidth.LARGE;
  employees: any;
  employeeCreditBalance: any;
  form: UntypedFormGroup;
  headers: any = this.buildHeaders();
  shownRows: any[] = [];
  allRows: any[] = [];
  showAll = false;
  loaded: boolean;
  panelClosedSubscription: Subscription;
  @ViewChild('tidyTable') tidyTable: any;

  constructor(
    private client: Client,
    private fb: UntypedFormBuilder,
    private navCtrl: CustomNavController,
    private employerService: EmployerService,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private tidyCurrencyPipe: TidyCurrencyPipe,
    private util: Util,
    private cdr: ChangeDetectorRef,
    public windowService: WindowService
  ) {
    this.form = this.fb.group({
      search: [''],
    });
    this.form.valueChanges.pipe(
      debounceTime(300)
    ).subscribe(val => this.updateSearch(val));
  }

  async ngOnInit() {
    this.getData();
  }

  async getData() {
    this.form.patchValue({search: ''});
    this.employees = await this.employerService.listEmployees();
    this.employeeCreditBalance = await this.employerService.getEmployeeCreditBalance();
    this.headers = this.buildHeaders();
    this.shownRows = this.buildRows(this.employees);
    this.allRows =this.shownRows;
    const billingData = await this.client.getBillingData();
    this.companyCredit = billingData.balance;
    this.loaded = true;
  }

  hideShowAll() {
    this.showAll = !this.showAll;
    this.shownRows = this.buildRows(this.employees);
  }

  buildHeaders() {
    return [
      'Employee',
      'Email',
      'Credit Balance',
      'Credit Used',
      'Status',
      'last-column'
    ];
  }

  buildRows(employees) {
    if (!employees?.length) {
      return [{
        'Employee': {},
        'Email': {},
        'Credit Balance': {},
        'Credit Used': {},
        'Status': {},
        'last-column': {}
      }]
    }
    const rows = employees.filter(employee => this.showAll ? true : !employee.inactivated_at)
      .map(employee => ({
        'Employee': {
          value: `${employee.source_user_first_name} ${employee.source_user_last_name}`,
          action: null,
          employeeCustomerId: employee.employee_customer_id
        },
        'Email': {
          value: employee.source_user_email,
          action: null
        },
        'Credit Balance': {
          value: this.getCreditBalance(employee.employee_customer_id),
          action: null
        },
        'Credit Used': {
          value: this.getCreditUsed(employee.employee_customer_id),
          action: null
        },
        'Status': {
          value: employee.inactivated_at ? 'Inactive' : 'Active',
          action: null
        },
        'last-column': {
          value: '',
          action: null,
          hasChevron: true
        },
      }));
    return rows;
  }

  getCreditBalance(clientId) {
    const balance = this.employeeCreditBalance?.find(client => clientId === client.customer.id);
    return this.tidyCurrencyPipe.transform(balance?.lifetime_amount_sent - balance?.lifetime_amount_used - balance?.lifetime_amount_cancelled);
  }

  getCreditUsed(clientId) {
    const balance = this.employeeCreditBalance?.find(client => clientId === client.customer.id);
    return this.tidyCurrencyPipe.transform(balance?.lifetime_amount_used);
  }

  getEmployeeBalance(clientId) {
    const balance = this.employeeCreditBalance?.find(client => clientId === client.customer.id);
    return balance;
  }

  goToEditEmployee(event) {
    const employee = this.employees.find(emp => emp.employee_customer_id === event.element['Employee'].employeeCustomerId);
    const employeeBalance = this.getEmployeeBalance(employee.employee_customer_id)
    const companyCredit = this.companyCredit;
    this.goToPage(EmployerEditEmployeePage, { employee, employeeBalance, companyCredit });
  }

  addEmployee() {
    this.goToPage(EmployerAddEmployeePage);
  }

  goToAddCredit() {
    this.goToPage(EmployerAddCreditPage);
  }

  goToPage(component, params = null) {
    this.storage.save('dialog-right-side-open', true);
    this.storage.save('dialog-params', params);
    this.rightSidePanelService.openRightSidePanel(component);
    this.panelClosedSubscription?.unsubscribe();
    this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
      this.showLoadingSpinner = true;
      await this.getData();
      this.showLoadingSpinner = false;
    });
  }

  sortChanged(sort) {
    // Helper function to convert currency string to number
    const convertCurrencyToNumber = (value: string): number => {
      if (!value || typeof value !== 'string') {return 0;}
      // Remove currency symbols, commas, and convert to number
      return parseFloat(value.replace(/[$,]/g, '')) || 0;
    };
    const isCurrencyColumn = sort.active === 'Credit Balance' || sort.active === 'Credit Used';
    if (sort.direction === 'asc' || sort.direction === '') {
      this.shownRows.sort((a, b) => {
        if (isCurrencyColumn) {
          const aValue = convertCurrencyToNumber(a[sort.active]?.value);
          const bValue = convertCurrencyToNumber(b[sort.active]?.value);
          return aValue > bValue ? 1 : -1;
        } else {
          return a[sort.active]?.value > b[sort.active]?.value ? 1 : -1;
        }
      });
    } else {
      this.shownRows.sort((a, b) => {
        if (isCurrencyColumn) {
          const aValue = convertCurrencyToNumber(a[sort.active]?.value);
          const bValue = convertCurrencyToNumber(b[sort.active]?.value);
          return aValue < bValue ? 1 : -1;
        } else {
          return a[sort.active]?.value < b[sort.active]?.value ? 1 : -1;
        }
      });
    }
  }

  updateSearch({search}) {
    if (search.length < 2) {
      return this.shownRows = this.allRows;
    }
    const term = search.toLowerCase();
    this.shownRows = this.allRows.filter((client) => {
      if (client['Employee'].value.toLowerCase().includes(term) || client['Email'].value.toLowerCase().includes(term)) {
        return client;
      }
    });
  }

  exportCSV() {
    if (!this.tidyTable) {
      this.util.showError('No data to export', 5000);
      return;
    }
    if (!this.shownRows || this.shownRows.length === 0) {
      this.util.showError('No data to export', 5000);
      return;
    }
    // Create a new MatTableDataSource with all rows to ensure all data is exported
    // This avoids pagination limitations that might exist in the table's filteredData
    const exportDataSource = new MatTableDataSource(this.shownRows);
    this.tidyTable.exportCsv(exportDataSource);
  }

}
